import type { RcFile } from 'antd/es/upload/interface';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import UploadModal from 'components/common/UploadModal';

import { DISPLAY_TYPES } from 'components/common/UploadModal/utils';
import type { DisplayTypeEnum } from 'components/common/UploadModal/utils';
import { MEDIAS_API_UPLOAD } from 'constants/ApiConstants';

import { STATUS_DELETED } from 'constants/MediaConstants';
import useMediaRules from 'hooks/useMediaRules';
import type { AllowedMedias } from 'hooks/useMediaRules';
import { downloadFetchedURL, generateClippingTransformation } from 'modules/medias/actions';
import type { AbstractMedia } from 'modules/medias/types/abstract-media';
import { getAdditionalMediaValidators, MEDIA_VALIDATORS } from 'services/FileUtils';
import { MediaGallery, MediasUploader } from 'ui/base';
import type { MediaListItem } from 'ui/base/MediaGallery/types';
import { allowedFileValidator, getError } from 'ui/base/MediasUploader/utils';
import useNotification from 'ui/base/Notification';

import Uploader from './components/Uploader';
import { galleryStyle } from './ui.style';

const DEFAULT_DISPLAY_TYPES = Object.values(DISPLAY_TYPES);

/**
 * UploadDragger - A comprehensive media upload component with drag & drop, gallery and modal features
 *
 * This component provides a complete media management solution including:
 * - Drag & drop file upload interface
 * - Media gallery display with preview
 * - Modal for selecting from media library or uploading new files
 * - Support for restrictions, clipping, and various file formats
 */
export type UploadDraggerProps = {
  /** Maximum number of files that can be uploaded. If not set, unlimited */
  limit?: number;
  /** Additional parameters sent with upload requests (e.g., entityType, subType) */
  params?: Record<string, unknown>;
  /** Base URL for upload operations. Defaults to MEDIAS_API_UPLOAD constant */
  uploadURL?: string;
  /** Endpoint used to link media library items to the entity. Defaults to `${uploadURL}/medias/import` */
  linkURL?: string;
  /** Disables all upload and interaction functionality */
  disabled?: boolean;
  /** Disables all items actions */
  readonly?: boolean;
  /** Hide the uploader */
  hideUploader?: boolean;
  /** Additional CSS classes to apply to the root container */
  className?: string;
  /** File type restrictions with extensions and MIME types */
  allowed: AllowedMedias;
  /** Enables media restriction toggle functionality */
  allowRestrict?: boolean;
  /** Enables media clipping/cropping functionality */
  allowClipping?: boolean;
  /** Delegates file persistence to parent form instead of immediate upload */
  delegateToForm?: boolean;
  /** Uses two-step upload process (get URL then upload) */
  isTwoStepUpload?: boolean;
  /** Current list of media items to display */
  medias?: AbstractMedia[];
  /** Available upload methods (COMPUTER, LIBRARY, TAKE_PICTURE) */
  displayTypes?: DisplayTypeEnum[];
  /** Custom filter function to exclude certain media items from display */
  filter?: (media: AbstractMedia) => boolean;
  /** Callback fired when media list changes (add, remove, update) */
  onChange?: (medias: AbstractMedia[]) => void;
  /** Persist media immediately on upload vs waiting for confirmation. Forwarded to UploadModal */
  persistAtUpload?: boolean;
  /** Additional data to include with upload requests. Forwarded to UploadModal */
  uploadData?: Record<string, unknown>;
  /** Display maximum file size message */
  displayMaxSizeMessage?: boolean;
  /** Filter media library items by type. Forwarded to UploadModal */
  librariesFilter?: { types?: string[] };
  /** Whether uploaded files should be saved to media library. Forwarded to UploadModal */
  uploadInLibrary?: boolean;
  /** Additional CSS classes for the modal container. Forwarded to UploadModal */
  modalClassName?: string;
};

const UploadDragger: React.FC<UploadDraggerProps> = ({
  limit,
  params,
  allowed,
  readonly,
  onChange,
  disabled,
  className,
  displayMaxSizeMessage = false,
  hideUploader,
  delegateToForm,
  filter: filterFn,
  medias: allMedias = [],
  uploadURL = MEDIAS_API_UPLOAD,
  allowRestrict,
  allowClipping,
  displayTypes: displayTypesProps = DEFAULT_DISPLAY_TYPES,
  isTwoStepUpload,
  ...props
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const notification = useNotification();
  const configuration = useMediaRules(allowed);

  const [autoBrowse, setAutoBrowse] = React.useState(false);
  const [defaultFiles, setDefaultFiles] = React.useState<RcFile[]>([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [defaultTab, setDefaultTab] = React.useState<DisplayTypeEnum>(DISPLAY_TYPES.COMPUTER);

  const medias = React.useMemo(() => {
    const tmp = typeof filterFn === 'function' ? allMedias.filter(filterFn) : allMedias || [];
    return tmp?.filter(({ status }) => status !== STATUS_DELETED);
  }, [allMedias, filterFn]);

  const isLimitReach = limit !== undefined && medias.length >= limit;
  const displayUploader = !hideUploader && !disabled && !isLimitReach;
  const nbMediaUploadable = limit ? limit - medias.length : undefined;

  // Override display types if camera is disallowed
  const displayTypes = React.useMemo(
    () =>
      displayTypesProps.reduce<DisplayTypeEnum[]>((acc, type) => {
        const exists = Object.values(DISPLAY_TYPES).includes(type);
        if (exists) {
          acc.push(type);
        }
        return acc;
      }, []),
    [displayTypesProps]
  );

  const handleOpenModal = React.useCallback(
    (tab: DisplayTypeEnum = DISPLAY_TYPES.COMPUTER, browse = false) => {
      setDefaultTab(tab);

      if (!disabled) {
        setAutoBrowse(browse);
        setIsModalOpen(true);
      }
    },
    [disabled]
  );

  const debouncedHandleOpenModal = useDebouncedCallback(handleOpenModal, 100);

  const handleBeforeUpload = React.useCallback(
    async (file: RcFile) => {
      const additionalValidators = getAdditionalMediaValidators(allowed);

      const error = await getError(file, [
        () => allowedFileValidator(file, intl, configuration?.extensions, configuration?.fileTypes),
        ...additionalValidators.map(validator => MEDIA_VALIDATORS[validator](configuration.config, intl)),
      ]);

      if (error) {
        notification.error({ title: error });
      } else {
        setDefaultFiles(files => [...files, file]);
        debouncedHandleOpenModal(DISPLAY_TYPES.COMPUTER);
      }
      return false;
    },
    [intl, debouncedHandleOpenModal, configuration, allowed, notification]
  );

  const handleOnCancel = React.useCallback(() => {
    setDefaultFiles([]);
    setIsModalOpen(false);
  }, []);

  const handleOnUploaded = React.useCallback(
    ({ uploadedFiles }: { uploadedFiles: AbstractMedia[] }) => {
      setDefaultFiles([]);
      onChange?.([...allMedias, ...uploadedFiles]);
      setIsModalOpen(false);
    },
    [onChange, allMedias]
  );

  const handleOnChange = React.useCallback(
    (mediaList: MediaListItem[]) => {
      // Note: used only like a callback when the user delete or restrict a media
      const baseMediaIds = medias.map(({ id }) => id);
      const newMedias = mediaList.reduce<Record<string, AbstractMedia>>(
        (acc, { data }) => ({ ...acc, [data.id]: data }),
        {}
      );

      const allNewMedias = allMedias.reduce<AbstractMedia[]>((acc, media) => {
        const isUpdated = !baseMediaIds.includes(media.id) || Boolean(newMedias[media.id]);
        if (isUpdated) {
          acc.push(newMedias[media.id] ?? media);
        }
        return acc;
      }, []);

      onChange?.(allNewMedias);
    },
    [onChange, allMedias, medias]
  );

  const handleOnDownload = React.useCallback(
    ({ data: abstractMedia }: MediaListItem) => {
      if (!abstractMedia.path) {
        return;
      }
      dispatch(downloadFetchedURL(abstractMedia.path, abstractMedia.filename ?? null));
    },
    [dispatch]
  );

  const handleOnRestrict = React.useCallback(
    (onUpdate: (media: MediaListItem) => void) => (media: MediaListItem) => {
      if (!disabled) {
        onUpdate({
          ...media,
          data: {
            ...media.data,
            mediaLibraryItemId: undefined,
            restricted: !media.data.restricted,
          },
        });
      }
    },
    [disabled]
  );

  const handleOnClipping = React.useCallback(
    (mediaId: string) => {
      dispatch(generateClippingTransformation(mediaId, uploadURL));
    },
    [dispatch, uploadURL]
  );

  const renderGallery = (
    mediaUploaderCollection: MediaListItem[],
    onUpdate?: (media: MediaListItem) => void,
    onDelete?: (media: MediaListItem) => void,
    onRetry?: (media: MediaListItem) => void
  ) => (
    <MediaGallery
      hideSection
      withPreview
      onRetry={onRetry}
      onDelete={onDelete}
      className={galleryStyle}
      showClipping={allowClipping}
      onDownload={handleOnDownload}
      mediaList={mediaUploaderCollection}
      onClipping={allowClipping ? handleOnClipping : undefined}
      onRestrict={allowRestrict && onUpdate ? handleOnRestrict(onUpdate) : undefined}
    />
  );

  return (
    <div className={classNames(className, { [galleryStyle]: medias.length > 0 })}>
      {displayUploader && (
        <Uploader
          allowed={allowed}
          displayTypes={displayTypes}
          onClick={debouncedHandleOpenModal}
          onBeforeUpload={handleBeforeUpload}
          displayMaxSizeMessage={displayMaxSizeMessage}
          size={medias.length > 0 ? 'small' : 'full'}
        />
      )}

      {/* Used only for rendering the media gallery */}
      <MediasUploader
        onlyRender
        limit={limit}
        params={params}
        isTwoStepUpload={isTwoStepUpload ?? true}
        medias={medias}
        allowed={allowed}
        readOnly={readonly}
        uploadURL={uploadURL}
        displayMaxSizeMessage={displayMaxSizeMessage}
        render={renderGallery}
        onUploaded={handleOnChange}
        delegateToForm={delegateToForm}
      />

      {isModalOpen && (
        <UploadModal
          open
          params={params}
          allowed={allowed}
          medias={defaultFiles}
          uploadURL={uploadURL}
          autoBrowse={autoBrowse}
          defaultTab={defaultTab}
          limit={nbMediaUploadable}
          displayMaxSizeMessage={displayMaxSizeMessage}
          onCancel={handleOnCancel}
          temporary={delegateToForm}
          displayTypes={displayTypes}
          onUploaded={handleOnUploaded}
          linkURL={`${uploadURL}/medias/import`}
          {...props}
        />
      )}
    </div>
  );
};

export default UploadDragger;
